import { IllegalRecordHandleState, IllegalRecordState, Subject, WarningState } from '@36node-fcp/core-sdk';
import { EyeOutlined, PlayCircleOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Image, message, Modal, Popconfirm, Row, Space, Spin } from 'antd';
import { includes, isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { BigPlayButton, Player } from 'video-react';

import { useIllegalRecordList } from 'src/features/illegal';
import { useSession } from 'src/features/session';
import { useWarning, WatchWarningMode } from 'src/features/warning';
import { ymdhms } from 'src/lib/lang/time';
import { checkPermission } from 'src/lib/privilege';
import { useApi } from 'src/lib/react-api';
import { fcp } from 'src/services';

import { IllegalHandleDropdown } from './illegal-handle.dropdown';
import { useMonitor } from './monitor.provider';
import { ContentEmpty } from './warning-drawer-empty';

type WarningDrawDetailProps = {};

const IllegalPanelDetail = ({ illegal, subject, setVideoSrc, setVideoModalOpen }) => (
  <>
    <Space size={10}>
      <div className="title">{illegal.name}</div>
      <Button
        size="small"
        type="link"
        icon={<SendOutlined />}
        onClick={() =>
          window.open(`/${subject === Subject.VEHICLE ? 'vehicle' : 'person'}/illegal/${illegal.id}`, '_blank')
        }
      >
        详情
      </Button>
    </Space>
    <div className="desc">
      <div>{`违法时间：${ymdhms(illegal.capAt)}`}</div>
      <div>{`违法地点：${illegal.place || ''}`}</div>
    </div>
    {illegal?.images?.length > 0 && (
      <Image.PreviewGroup>
        <Row gutter={16}>
          {illegal?.images?.map((url) => (
            <Col span={3} key={url}>
              <Image preview={{ mask: <EyeOutlined /> }} src={url} width="40px" height="40px" key="image" alt="image" />
            </Col>
          ))}
          {illegal?.plateImages?.map((url) => (
            <Col span={3} key={url}>
              <Image preview={{ mask: <EyeOutlined /> }} src={url} width="40px" height="40px" key="image" alt="image" />
            </Col>
          ))}
          {illegal?.videos?.map((url) => (
            <Col span={3} key={url}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  setVideoSrc(url);
                  setVideoModalOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setVideoSrc(url);
                    setVideoModalOpen(true);
                  }
                }}
                style={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'cente',
                  cursor: 'pointer',
                }}
              >
                <PlayCircleOutlined style={{ fontSize: 20 }} />
              </div>
            </Col>
          ))}
        </Row>
      </Image.PreviewGroup>
    )}
  </>
);

export const WarningDrawDetail: React.FC<WarningDrawDetailProps> = () => {
  const session = useSession();
  const [{ settings }] = useMonitor();
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const warningId = settings.selected?.id || '';
  const plate = settings.selected?.plate || '';
  const [{ result: warning }, getWarning] = useWarning({ warningId });

  // TODO: 未选中时也会请求一次违法列表，待修复
  const [{ result: illegals = [] }] = useIllegalRecordList(
    plate && { _limit: 1000, plate, state: [IllegalRecordState.CLOSED], _sort: '-updateAt' }
  ); // 获取已关闭违法记录

  const [closeState, closeWarning] = useApi(fcp.closeWarning, {
    onSuccess: () => {
      message.success('预警关闭成功');
      getWarning({ warningId });
    },
    onFailure: (err) => {
      message.error(`删除失败: ${err.message}`);
    },
  });

  const [closeIllegalState, closeIllegal] = useApi(fcp.closeIllegal, {
    onSuccess: () => {
      message.success('违法关闭成功');
      getWarning({ warningId });
    },
    onFailure: (err) => {
      message.error(`关闭失败: ${err.message}`);
    },
  });

  const [, updateIllegal] = useApi(fcp.updateIllegalRecord, {
    onSuccess: () => {
      message.success('违法处理成功');
      getWarning({ warningId });
    },
    onFailure: (err) => {
      message.error(`关闭失败: ${err.message}`);
    },
  });

  const showCloseIllegals = useMemo(() => {
    if (isEmpty(illegals)) {
      return [];
    }
    const { motor, nonMotor, watchMode } = settings;
    if (watchMode === WatchWarningMode.ALL) {
      return illegals.filter((illegal) => includes([...motor.codes, ...nonMotor.codes], illegal.code));
    } else if (watchMode === WatchWarningMode.MOTOR) {
      return illegals.filter((illegal) => includes(motor.codes, illegal.code));
    }
    return illegals.filter((illegal) => includes(nonMotor.codes, illegal.code));
  }, [illegals]);

  if (warningId && !warning) {
    return <Spin />;
  }

  return (
    <>
      <Content>
        <div className="custom-header">卡片详情</div>
        <div className="detail-content">
          {warningId ? (
            <div className="details-content-desc">
              <WarningPanelBox>
                <div className="left">
                  <Space size={10}>
                    <div className="title">{warning.plate}</div>
                    <Button
                      size="small"
                      type="link"
                      icon={<SendOutlined />}
                      onClick={() =>
                        window.open(
                          `/${warning.subject === Subject.VEHICLE ? 'vehicle' : 'person'}/warning/${warning.id}`,
                          '_blank'
                        )
                      }
                    >
                      详情
                    </Button>
                  </Space>
                  <div className="desc">
                    <div>{`预警时间：${ymdhms(warning.capAt)}`}</div>
                    <div>{`经过卡口：${warning.gantry?.name}`}</div>
                  </div>
                </div>
                {checkPermission(session, ['person:peopleAlertAd']) &&
                  (warning.state === WarningState.OPEN ? (
                    <Popconfirm
                      title="确认已处理该预警，对应的违法将会关闭。"
                      onConfirm={() => closeWarning({ warningId })}
                    >
                      <Button
                        className="handle-btn"
                        loading={closeState.loading}
                        disabled={warning.state !== WarningState.OPEN}
                      >
                        处理预警
                      </Button>
                    </Popconfirm>
                  ) : (
                    <Button className="handle-btn" disabled>
                      已处理
                    </Button>
                  ))}
              </WarningPanelBox>
              {warning.illegals.length ? (
                <>
                  <Divider style={{ marginTop: 16, marginBottom: 12, borderColor: '#E4E4E7' }} />
                  {warning.illegals.map((illegal, index) => (
                    <>
                      <IllegalPanelBox key={index}>
                        <div className="left">
                          <IllegalPanelDetail
                            illegal={illegal}
                            subject={warning.subject}
                            setVideoSrc={setVideoSrc}
                            setVideoModalOpen={setVideoModalOpen}
                          />
                        </div>
                        <div className="right">
                          {checkPermission(session, ['vehicle:carIllegalAd']) &&
                            (illegal.state === IllegalRecordState.CLOSED ? (
                              <Button className="handle-btn" disabled>
                                已关闭
                              </Button>
                            ) : (
                              <Popconfirm
                                title="你确定要关闭此违法吗？"
                                onConfirm={() => closeIllegal({ illegalRecordId: illegal.id })}
                              >
                                <Button
                                  className="handle-btn"
                                  type="text"
                                  style={{ border: '1px solid #D9D9D9' }}
                                  loading={closeIllegalState.loading}
                                >
                                  关闭违法
                                </Button>
                              </Popconfirm>
                            ))}
                          <div style={{ marginTop: 10 }}>
                            {illegal.handleState === IllegalRecordHandleState.CLOSED ? (
                              <Button className="handle-btn" disabled>
                                已处置
                              </Button>
                            ) : (
                              <IllegalHandleDropdown
                                className="handle-btn"
                                handleState={illegal.handleState}
                                onConfirm={({ handleState, handleResults, handleDescription }) => {
                                  updateIllegal({
                                    illegalRecordId: illegal.id,
                                    body: {
                                      handleState,
                                      ...(!isEmpty(handleResults) && { handleResults }),
                                      ...(handleDescription && { handleDescription }),
                                    },
                                  });
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </IllegalPanelBox>
                      {index < warning.illegals.length - 1 && (
                        <Divider dashed style={{ marginTop: 12, marginBottom: 12, borderColor: '#E4E4E7' }} />
                      )}
                    </>
                  ))}
                </>
              ) : null}

              {showCloseIllegals.length ? (
                <>
                  <Divider style={{ marginTop: 16, marginBottom: 12, borderColor: '#000', fontSize: 14 }}>
                    已关闭违法
                  </Divider>
                  {showCloseIllegals.map((illegal, index) => (
                    <>
                      <IllegalPanelBox key={index}>
                        <div className="left">
                          <IllegalPanelDetail
                            illegal={illegal}
                            subject={warning.subject}
                            setVideoSrc={setVideoSrc}
                            setVideoModalOpen={setVideoModalOpen}
                          />
                        </div>
                      </IllegalPanelBox>
                      {index < showCloseIllegals.length - 1 && (
                        <Divider dashed style={{ marginTop: 12, marginBottom: 12, borderColor: '#E4E4E7' }} />
                      )}
                    </>
                  ))}
                </>
              ) : null}
            </div>
          ) : (
            <ContentEmpty title="请选择报警卡片" />
          )}
        </div>
      </Content>

      <Modal
        open={videoModalOpen}
        footer={null}
        onCancel={() => {
          setVideoModalOpen(false);
          setVideoSrc(null);
        }}
        centered
        width={800}
        styles={{
          body: {
            padding: 8,
          },
        }}
      >
        {videoSrc && (
          <Player src={videoSrc} autoPlay fluid>
            <BigPlayButton position="center" />
          </Player>
        )}
      </Modal>
    </>
  );
};

const WarningPanelBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  .left {
    width: 406px;
    display: flex;
    flex-direction: column;
  }

  .title {
    color: #09090b;
    font-size: 16px;
    font-weight: 600;
  }

  .desc {
    color: #71717a;
  }
`;

const IllegalPanelBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  .left {
    width: 406px;
    display: flex;
    flex-direction: column;
  }

  .title {
    color: #09090b;
    font-weight: 500;
  }

  .desc {
    color: #71717a;
    margin-bottom: 8px;
  }

  .right {
    display: flex;
    flex-direction: column;
  }
`;

const Content = styled.div`
  margin-left: 10px;
  flex: 1;

  .detail-content {
    width: 530px;
    height: 740px;
    padding: 16px;
    border: 1px solid #e4e4e7;
    border-radius: 6px;
    background: #f4f4f5;

    display: flex;
    flex-direction: column;
    align-items: center;

    overflow-x: hidden;
    overflow-y: auto;

    .details-content-desc {
      width: 100%;
      box-sizing: border-box;
      padding: 16px;
      border-radius: 12px;
      border: 1px solid #e5e7eb;
      background: #fff;
    }
  }

  .ant-btn-icon:not(:last-child) {
    margin-right: 4px !important;
  }
`;
